import { createContext, useContext, useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { LayoutDashboard, Users, FileText, Briefcase, Receipt, CreditCard, Wallet, Images, BarChart3, Settings, Menu, Plus, LogOut, Camera, X, LifeBuoy, AlertTriangle } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/common";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { ExpenseDialog } from "@/components/dialogs/ExpenseDialog";
import { CustomerDialog } from "@/components/dialogs/CustomerDialog";
import { JobDialog } from "@/components/dialogs/JobDialog";
import { PaymentDialog } from "@/components/dialogs/PaymentDialog";
import { BillingBanner } from "@/components/BillingBanner";
import { RefreshButton, UpdateBanner } from "@/components/AppShell";

const NAV = [
  ["/", "Dashboard", LayoutDashboard], ["/customers", "Customers", Users], ["/quotes", "Quotes", FileText], ["/jobs", "Jobs", Briefcase],
  ["/invoices", "Invoices", Receipt], ["/payments", "Payments", CreditCard], ["/expenses", "Expenses", Wallet], ["/receipts", "Receipts", Images],
  ["/reports", "Reports", BarChart3], ["/settings", "Settings", Settings], ["/support", "Help & Support", LifeBuoy],
];

const QuickCtx = createContext(null);
export const useQuick = () => useContext(QuickCtx);
export const useBusiness = () => useQuery({ queryKey: ["settings"], queryFn: () => api.get("/settings").then((r) => r.data) });

const NavList = ({ onNavigate }) => (
  <nav className="flex flex-col gap-0.5 px-3" data-testid="main-nav">
    {NAV.map(([to, label, Icon]) => (
      <NavLink key={to} to={to} end={to === "/"} onClick={onNavigate} data-testid={`nav-${label.toLowerCase()}`} className={({ isActive }) => `nav-item ${isActive ? "active" : ""}`}>
        <Icon size={18} strokeWidth={2} /> {label}
      </NavLink>
    ))}
  </nav>
);

export default function Layout() {
  const { user, logout } = useAuth();
  const { data: biz } = useBusiness();
  const nav = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [fabOpen, setFabOpen] = useState(false);
  const [dlg, setDlg] = useState({ kind: null, props: {} });
  const open = (kind, props = {}) => { setFabOpen(false); setDlg({ kind, props }); };
  const close = () => setDlg({ kind: null, props: {} });
  useEffect(() => {
    // After a session-expired re-login, reopen the New Customer form with its preserved draft; return-to keys are consumed here.
    sessionStorage.removeItem("neec_return_to");
    sessionStorage.removeItem("neec_session_expired");
    if (sessionStorage.getItem("neec_draft_customer")) setDlg({ kind: "customer", props: {} });
  }, []);
  const quick = {
    newQuote: () => { setFabOpen(false); nav("/quotes/new"); },
    newJob: (p) => open("job", p), newExpense: (p) => open("expense", p), newCustomer: (p) => open("customer", p),
    newPayment: (p) => open("payment", p), receiptPhoto: () => open("expense", { camera: true }),
  };
  const QUICK = [
    ["New Quote", quick.newQuote, "quick-action-new-quote"], ["New Job", () => quick.newJob(), "quick-action-new-job"],
    ["New Expense", () => quick.newExpense(), "quick-action-new-expense"], ["New Customer", () => quick.newCustomer(), "quick-action-new-customer"],
  ];

  const Brand = () => (
    <div className="flex items-center gap-3 px-5 py-5">
      <Logo business={biz} size={40} />
      <div className="min-w-0">
        <div className="font-display font-bold text-white text-lg leading-tight truncate" data-testid="sidebar-business-name">{biz?.business_name || "NEEC Service"}</div>
        <div className="text-[11px] text-slate-400 truncate">{biz?.city ? `${biz.city}, ${biz.state}` : "Field service"}</div>
      </div>
    </div>
  );

  return (
    <QuickCtx.Provider value={quick}>
      <div className="min-h-screen flex bg-[#F8FAFC]">
        <aside className="hidden md:flex flex-col w-60 shrink-0 bg-[#0B192C] text-white fixed inset-y-0 left-0 z-30" data-testid="desktop-sidebar">
          <Brand />
          <NavList />
          <div className="mt-auto p-4 border-t border-white/10 space-y-3">
            <RefreshButton />
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-full bg-orange-500 grid place-items-center font-bold text-sm">{(user?.name || "?").split(" ").map((s) => s[0]).join("").slice(0, 2)}</div>
              <div className="min-w-0 flex-1"><div className="text-sm font-semibold truncate">{user?.name}</div><div className="text-[11px] text-slate-400 truncate">{biz?.owner_name === user?.name ? "Owner" : user?.email}</div></div>
              <button onClick={logout} data-testid="logout-button" className="text-slate-400 hover:text-white transition-colors" title="Log out"><LogOut size={16} /></button>
            </div>
          </div>
        </aside>

        <div className="flex-1 md:ml-60 min-w-0 flex flex-col">
          <header className="md:hidden sticky top-0 z-30 bg-[#0B192C] text-white flex items-center justify-between px-2 h-14" data-testid="mobile-header">
            <button onClick={() => setMenuOpen(true)} data-testid="mobile-menu-button" className="w-11 h-11 grid place-items-center rounded-lg hover:bg-white/10" aria-label="Menu"><Menu size={22} /></button>
            <div className="flex items-center gap-2 min-w-0"><Logo business={biz} size={28} /><span className="font-display font-bold truncate">{biz?.business_name || "NEEC Service"}</span></div>
            <div className="flex items-center"><RefreshButton compact /><button onClick={logout} data-testid="mobile-logout-button" className="w-11 h-11 grid place-items-center rounded-lg hover:bg-white/10" aria-label="Log out"><LogOut size={18} /></button></div>
          </header>
          <main className="flex-1 px-4 sm:px-6 lg:px-10 py-5 sm:py-8 pb-28 md:pb-10 max-w-7xl w-full mx-auto">
            <BillingBanner user={user} />
            <Outlet />
          </main>
          <UpdateBanner />
        </div>

        <Sheet open={menuOpen} onOpenChange={setMenuOpen}>
          <SheetContent side="left" className="w-72 p-0 bg-[#0B192C] border-none text-white" data-testid="mobile-nav-drawer">
            <Brand />
            <NavList onNavigate={() => setMenuOpen(false)} />
          </SheetContent>
        </Sheet>

        <div className="md:hidden fixed bottom-5 right-5 z-40 flex flex-col items-end gap-2">
          {fabOpen && (
            <div className="flex flex-col items-end gap-2 fade-up" data-testid="fab-menu">
              {QUICK.map(([label, fn, tid]) => (
                <button key={label} onClick={fn} data-testid={tid} className="bg-white text-slate-900 shadow-lg border border-slate-200 rounded-full px-4 py-2.5 text-sm font-semibold flex items-center gap-2 hover:bg-slate-50">
                  <Plus size={16} className="text-orange-500" /> {label}
                </button>
              ))}
              <button onClick={quick.receiptPhoto} data-testid="quick-action-receipt-photo" className="bg-white text-slate-900 shadow-lg border border-slate-200 rounded-full px-4 py-2.5 text-sm font-semibold flex items-center gap-2 hover:bg-slate-50">
                <Camera size={16} className="text-orange-500" /> Receipt Photo
              </button>
            </div>
          )}
          <button onClick={() => setFabOpen((v) => !v)} data-testid="fab-button" className="w-14 h-14 rounded-full btn-orange grid place-items-center shadow-xl shadow-orange-500/30 transition-transform active:scale-95">
            {fabOpen ? <X size={26} /> : <Plus size={28} />}
          </button>
        </div>

        <ExpenseDialog open={dlg.kind === "expense"} onClose={close} {...dlg.props} />
        <CustomerDialog open={dlg.kind === "customer"} onClose={close} {...dlg.props} />
        <JobDialog open={dlg.kind === "job"} onClose={close} {...dlg.props} />
        <PaymentDialog open={dlg.kind === "payment"} onClose={close} {...dlg.props} />
      </div>
    </QuickCtx.Provider>
  );
}
